import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "@/lib/api";
import { Download, Headphones, FileText, Receipt, BookOpen, Lock, AlertCircle } from "lucide-react";

/**
 * Guest access page. Rendered from the secure link emailed to buyers who
 * checked out without an account. The URL segment IS the credential:
 *   /access/:token
 * The backend endpoint /api/access/:token returns the order + items scoped
 * to just what this order paid for. Everything on this page (downloads,
 * receipt, streaming) uses token-scoped endpoints — never the JWT-gated
 * /library ones — so the reader never needs to log in.
 */
export default function GuestAccess() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const res = await fetch(`${API}/access/${encodeURIComponent(token)}`);
        if (!res.ok) throw new Error((await res.json())?.detail || "Access link invalid.");
        const json = await res.json();
        if (!stop) setData(json);
      } catch (e) {
        if (!stop) setErr(e.message || "Failed to load your order.");
      }
    })();
    return () => { stop = true; };
  }, [token]);

  if (err) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="guest-access-error">
        <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-6">
          <AlertCircle className="w-6 h-6 text-red-500" />
        </div>
        <div className="eyebrow mb-3 text-red-500">Link Invalid</div>
        <h1 className="aperture-h1 text-3xl md:text-4xl mb-6">We couldn&apos;t open your order.</h1>
        <p className="text-navy/60 max-w-md mx-auto leading-relaxed">{err}</p>
        <p className="text-sm text-navy/50 mt-6">
          Need help? Email <a href="mailto:support@apertureent.com" className="text-gold underline">support@apertureent.com</a>
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="guest-access-loading">
        <div className="eyebrow mb-3">Loading</div>
        <div className="font-serif text-2xl text-navy">Opening your order…</div>
      </div>
    );
  }

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };
  const receiptUrl = `${API}/access/${encodeURIComponent(token)}/receipt.pdf`;
  const dlPdfUrl = (id, ver) => `${API}/access/${encodeURIComponent(token)}/download/${id}${ver ? `?v=${encodeURIComponent(ver)}` : ""}`;
  const dlAudioUrl = (id, ver) => `${API}/access/${encodeURIComponent(token)}/download-audio/${id}${ver ? `?v=${encodeURIComponent(ver)}` : ""}`;
  const streamAudioUrl = (id, ver) => `${API}/access/${encodeURIComponent(token)}/audio/${id}${ver ? `?v=${encodeURIComponent(ver)}` : ""}`;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-16" data-testid="guest-access-page">
      <div className="eyebrow mb-3 flex items-center gap-2">
        <Lock className="w-3 h-3" /> Secure Access
      </div>
      <h1 className="aperture-h1 text-4xl md:text-5xl mb-4">Your materials.</h1>
      <p className="text-navy/60 max-w-2xl mb-2">
        Downloaded straight from your order — no account needed. Bookmark this page or
        keep the confirmation email; the link is yours to use anytime.
      </p>
      <div className="text-xs text-navy/50 mb-12">
        Order #{(data.session_id || "").slice(-14)} · {fmtDate(data.created_at)} ·
        <a href={receiptUrl} target="_blank" rel="noopener noreferrer"
           data-testid="guest-receipt-link"
           className="ml-1 text-navy hover:text-gold underline underline-offset-4">
          <Receipt className="inline w-3 h-3 -mt-0.5" /> Receipt PDF
        </a>
      </div>

      <div className="grid md:grid-cols-2 gap-6" data-testid="guest-items-grid">
        {data.items.map((i) => (
          <div key={`${i.ebook_id}-${i.format}`}
               data-testid={`guest-item-${i.ebook_id}`}
               className="bg-white p-6 shadow-lg rounded-sm flex gap-5"
               style={{ border: "1px solid rgba(10,37,64,0.06)" }}>
            <div className="w-24 h-32 bg-[#F7F5F0] rounded-sm flex-shrink-0 flex items-center justify-center p-2">
              {i.cover ? <img src={i.cover} alt={i.title} className="max-w-full max-h-full object-contain" />
                       : <BookOpen className="w-6 h-6 text-navy/30" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg text-navy leading-tight">{i.title}</div>
              {i.author && <div className="text-xs text-navy/50 mt-0.5">{i.author}</div>}
              <div className="mt-4 space-y-2">
                {i.grants_ebook && i.has_pdf && (
                  <>
                    <a href={dlPdfUrl(i.ebook_id, i.pdf_ver)}
                       data-testid={`guest-download-pdf-${i.ebook_id}`}
                       className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-navy text-white text-[11px] uppercase tracking-widest rounded-sm hover:bg-gold transition-colors">
                      <Download className="w-3.5 h-3.5" /> Download eBook (PDF)
                    </a>
                    <a href={`${API}/access/${encodeURIComponent(token)}/read/${i.ebook_id}${i.pdf_ver ? `?v=${encodeURIComponent(i.pdf_ver)}` : ""}`}
                          data-testid={`guest-read-pdf-${i.ebook_id}`}
                          target="_blank" rel="noopener noreferrer"
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border text-navy text-[11px] uppercase tracking-widest rounded-sm hover:border-gold hover:text-gold transition-colors"
                          style={{ borderColor: "rgba(10,37,64,0.15)" }}>
                      <FileText className="w-3.5 h-3.5" /> Read Online
                    </a>
                  </>
                )}
                {i.grants_audio && i.has_audio_file && (
                  <>
                    <audio controls preload="none" src={streamAudioUrl(i.ebook_id, i.audio_ver)}
                           data-testid={`guest-audio-player-${i.ebook_id}`}
                           className="w-full h-9 mt-1" />
                    <a href={dlAudioUrl(i.ebook_id, i.audio_ver)}
                       data-testid={`guest-download-audio-${i.ebook_id}`}
                       className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border text-navy text-[11px] uppercase tracking-widest rounded-sm hover:border-gold hover:text-gold transition-colors"
                       style={{ borderColor: "rgba(10,37,64,0.15)" }}>
                      <Headphones className="w-3.5 h-3.5" /> Download Audiobook
                    </a>
                  </>
                )}
                {!(i.grants_ebook && i.has_pdf) && !(i.grants_audio && i.has_audio_file) && (
                  <div className="text-xs text-navy/50 italic">Files are being prepared — refresh in a moment.</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Soft nudge to create an account, never a gate. */}
      <div className="mt-16 p-8 bg-[#F7F5F0] rounded-sm flex flex-col md:flex-row items-center gap-6" data-testid="guest-nudge-block">
        <div className="flex-1">
          <div className="eyebrow mb-2">Optional</div>
          <div className="font-serif text-xl text-navy">Save your library for later.</div>
          <p className="text-sm text-navy/60 mt-2 leading-relaxed">
            Create a free account with the same email ({data.email}) and this order —
            plus any future purchases — will live in one place. Resume where you left off
            on any device.
          </p>
        </div>
        <Link to={`/register?email=${encodeURIComponent(data.email || "")}`}
              data-testid="guest-register-cta"
              className="btn-primary px-6 py-3 text-xs uppercase tracking-widest rounded-sm whitespace-nowrap">
          Create Free Account
        </Link>
      </div>
    </div>
  );
}
